import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, GraduationCap, Search, Users, User, Shield, CheckSquare, Bell } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function TopNav() {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/notifications?limit=10').then(r => {
        setNotifs(r.data.notifications || []);
        setUnread(r.data.unread_count || 0);
      }).catch(() => {});
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markAllRead = async () => {
    await api.post('/notifications/read-all').catch(() => {});
    setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread(0);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navLink = (path, label, icon, testId) => (
    <Link to={path}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
        isActive(path) ? 'bg-[#E8F5E9] text-[#4CAF50]' : 'text-[#57534E] hover:text-[#1C1917] hover:bg-[#F5F5F4]'
      }`}
      data-testid={testId}
    >
      {icon}
      <span className="hidden lg:inline">{label}</span>
    </Link>
  );

  const formatTime = (ts) => {
    if (!ts) return '';
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <header className="hidden md:block sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E7E5E4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-5">
            <Link to="/" className="flex items-center gap-2" data-testid="topnav-logo">
              <img src="https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/up9h807k_7ba9c841-3c9f-45da-b619-44a6f0b3d5bf.JPG" alt="LámFon" className="h-8 w-auto" />
            </Link>
            <nav className="flex items-center gap-0.5">
              {navLink('/', 'Home', <Home className="w-4 h-4" />, 'topnav-home')}
              {navLink('/learn', 'Learn', <GraduationCap className="w-4 h-4" />, 'topnav-learn')}
              {navLink('/search', 'Search', <Search className="w-4 h-4" />, 'topnav-search')}
              {isAuthenticated && (
                <>
                  {navLink('/community', 'Community', <Users className="w-4 h-4" />, 'topnav-community')}
                  {navLink('/validate', 'Validate', <CheckSquare className="w-4 h-4" />, 'topnav-validate')}
                  {isAdmin && navLink('/admin', 'Admin', <Shield className="w-4 h-4" />, 'topnav-admin')}
                </>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <div className="relative" ref={dropdownRef}>
                <button onClick={() => setShowNotifs(!showNotifs)}
                  className="relative p-2 rounded-xl text-[#57534E] hover:bg-[#F5F5F4] transition-colors"
                  data-testid="notification-bell">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-[#4CAF50] text-white text-[9px] font-bold rounded-full flex items-center justify-center min-w-[18px] px-1"
                      data-testid="notification-count">
                      {unread}
                    </span>
                  )}
                </button>

                {showNotifs && (
                  <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-2xl border border-[#E7E5E4] shadow-xl z-50 overflow-hidden" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7E5E4]">
                      <span className="text-sm font-semibold text-[#1C1917]">Notifications</span>
                      {unread > 0 && (
                        <button onClick={markAllRead} className="text-[10px] text-[#4CAF50] font-semibold" data-testid="mark-all-read">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifs.length > 0 ? notifs.map(n => (
                        <Link key={n.notification_id} to={n.link || '#'}
                          onClick={() => {
                            setShowNotifs(false);
                            if (!n.is_read) api.post(`/notifications/read/${n.notification_id}`).catch(() => {});
                          }}
                          className={`block px-4 py-3 border-b border-[#E7E5E4] last:border-0 hover:bg-[#FAFAF9] transition-colors ${!n.is_read ? 'bg-[#FDF6F0]' : ''}`}
                          data-testid={`notif-${n.notification_id}`}>
                          <div className="flex items-start gap-2">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.is_read ? 'bg-[#4CAF50]' : 'bg-transparent'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-[#1C1917] truncate">{n.title}</p>
                              <p className="text-[10px] text-[#57534E] mt-0.5 line-clamp-2">{n.body}</p>
                              <span className="text-[9px] text-[#A8A29E]">{formatTime(n.created_at)}</span>
                            </div>
                          </div>
                        </Link>
                      )) : (
                        <div className="px-4 py-6 text-center">
                          <p className="text-xs text-[#A8A29E]">No notifications yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isAuthenticated ? (
              <Link to="/profile"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive('/profile') ? 'bg-[#E8F5E9] text-[#4CAF50]' : 'text-[#57534E] hover:bg-[#F5F5F4]'
                }`}
                data-testid="topnav-profile">
                <div className="w-7 h-7 rounded-full bg-[#E8F5E9] flex items-center justify-center text-xs font-bold text-[#4CAF50]">
                  {user?.name?.charAt(0) || <User className="w-3.5 h-3.5" />}
                </div>
                <span className="hidden lg:inline">{user?.name?.split(' ')[0]}</span>
              </Link>
            ) : (
              <div className="flex gap-2">
                <Link to="/login" data-testid="topnav-login">
                  <button className="px-4 py-1.5 text-sm font-semibold text-[#57534E] hover:text-[#1C1917]">Sign In</button>
                </Link>
                <Link to="/register" data-testid="topnav-register">
                  <button className="px-4 py-1.5 text-sm font-semibold bg-[#4CAF50] text-white rounded-xl hover:bg-[#3D8B40]">Sign Up</button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
